<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row class="align-items-center">

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />

          <Button
            :label="$t('Show Summary')"
            size="md"
            show-icon
            class="mr-1"
            :disabled="disabled"
            @click="fetchProvisionSummary"
          />

          <Button
            :label="$t('Post Provision')"
            size="md"
            show-icon
            :disabled="disabled"
            @click="postProvision"
          />

          <SwitchButton
            v-model="makeFullProvision"
            class="mt-2 ml-1"
            :label="$t('Make Full Provision')"
            name="make-full-provision"
            :on-change="onMakeFullProvision"
            :readonly="disabled"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >

          <div class="d-flex align-items-center justify-content-lg-end mb-1 mb-md-0">
            <b-dropdown
              id="dropdown-1"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :text="$t('Export')"
              variant="primary"
              class="mr-1"
              :disabled="isDisabled"
            >
              <b-dropdown-item @click="csvExport">
                {{ $t('CSV') }}
              </b-dropdown-item>
              <b-dropdown-item @click="excelExport">
                {{ $t('Excel') }}
              </b-dropdown-item>
            </b-dropdown>

            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />

            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="provisionedOverdraftRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="provisionedOverdraftList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(select)="data">
        <b-form-group class="mt-2">
          <b-form-checkbox
            v-model="data.item.select"
          />
        </b-form-group>
      </template>

      <template #cell(amountOverdrawn)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(accruedODInterest)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(amountAboveLimit)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(expiryDate)="data">
        {{ data.value ? toDateWithYearFirst(data.value) : '' }}
      </template>

      <template #cell(lastProvisionedAmount)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(expectedProvisionAmount)="data">
        {{ toBalance(data.value) }}
      </template>
      <template #cell(lastProvisionedDate)="data">
        {{ data.value ? toDateWithYearFirst(data.value) : '' }}
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      ok-only
      ok-variant="outline-secondary"
      :ok-title="$t('Close')"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Summary')"
    >
      <b-table
        ref="summaryRef"
        :busy="isBusy"
        :items="summaryList"
        :per-page="pageSize"
        :current-page="currentPage"
        responsive
        :fields="translatedSummaryTableHeaders"
        primary-key="id"
        show-empty
        :empty-text="$t('No records available')"
        class="position-relative"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>
      </b-table>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="overdraftProvisioningRules">
        <b-form>
          <b-row class="match-height">
            <!-- Loan Account No -->
            <b-col md="6">
              <InputField
                id="casa-account-no"
                v-model="filter.accountNumber"
                :label="$t('CASA Account No')"
                :placeholder="$t('Enter loan account no')"
                type="text"
              />
            </b-col>
            <!-- Loan Account No -->

            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :label="$t('Product')"
                :placeholder="$t('Select Product')"
                :options="productOptions"
                :reduce="option => option.value"
                item="label"
              />
            </b-col>
            <!-- Product -->

            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :label="$t('Branch')"
                :placeholder="$t('Select Branch')"
                :options="branchOptions"
                :reduce="option => option.value"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->

            <!-- Overdraft Classification -->
            <b-col md="6">
              <SelectField
                id="Overdraft Classification"
                v-model="filter.classificationStatus"
                :label="$t('Overdraft Classification')"
                :placeholder="$t('Select Overdraft Classification')"
                :options="classificationOptions"
                :reduce="option => option.value"
                item="label"
              />
            </b-col>
            <!-- Overdraft Classification -->

            <!-- Provision Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date"
                v-model="filter.provisionDate"
                :label="$t('Provision Date')"
                :min-date="new Date()"
                :config="datePickerConfig"
                :placeholder="$t('Select provision date')"
                :error-name="$t('Provision Date')"
                rules="required"
              />
            </b-col>
            <!-- Provision Date -->
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BFormGroup, BFormCheckbox, BDropdown, BDropdownItem, BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapGetters, mapActions } from 'vuex'
import { required, minPhoneVal, maxPhoneVal } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { permissionMixin } from '@core/mixins/permission'
import { toBalance, formatAmount, toFormatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import { gender, accessLevel, classificationLists } from '../../../../helpers/data'
import {
  toDateWithYearFirst, getMinusOneDayDate,
} from '@/helpers/formatDate'
import getStatusColor from '../../../../helpers/getStatusColor'
import Button from '../../../components/button/Button.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import { accountPermissions } from '@/helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BFormGroup,
    BFormCheckbox,
    TablePagination,
    BModal,
    BSpinner,
    BDropdown,
    BDropdownItem,
    ValidationObserver,
    TableEntries,
    SelectField,
    InputField,
    Button,
    FullScreenSpinner,
    SwitchButton,
    DatePickerField,
    BForm,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toFormatAmount,
      formatAmount,
      toBalance,
      toDateWithYearFirst,
      isReadonly: false,
      isEditable: false,
      isFullName: false,
      showSpinner: false,
      isDisabled: false,
      isBusy: false,
      sortBy: 'name',
      required,
      minPhoneVal,
      maxPhoneVal,
      modalTitle: this.$t('Create Account Officer'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'select', sortable: true, label: 'Select' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Loan Account No' },
        { key: 'product', sortable: true, label: 'Product Name' },
        { key: 'branch', sortable: true, label: 'Branch' },
        // { key: 'overdraftLimit', sortable: true },
        { key: 'amountOverdrawn', sortable: true, label: 'Amount Overdrawn' },
        { key: 'accruedODInterest', sortable: true, label: 'Accrued OD Interest' },
        { key: 'amountAboveLimit', sortable: true, label: 'Amount Above Limit' },
        { key: 'numberOfDaysAboveLimit', sortable: true, label: 'Number Of Days Above Limit' },
        { key: 'expiryDate', sortable: true, label: 'Expiry Date' },
        { key: 'daysPastDue', sortable: true, label: 'Days Past Due' },
        { key: 'classificationStatus', sortable: true, label: 'OD Status Classification' },
        { key: 'lastProvisionedAmount', sortable: true, label: 'Last Provisioned Amount' },
        { key: 'expectedProvisionAmount', sortable: true, label: 'Expected Provision Amount' },
        { key: 'increaseOrDecrease', sortable: true, label: 'Increase/Decrease in Provision' },
        { key: 'lastProvisionedDate', sortable: true, label: 'Last Provisioned Date' },
      ],
      summaryTableHeaders: [
        { key: 'classificationStatus', sortable: true, label: 'Classification Status' },
        { key: 'expectedProvisionAmount', sortable: true, label: 'Expected Provision Amount' },
        { key: 'totalPrincipalOutstanding', sortable: true, label: 'Total Principal Outstanding' },
      ],
      makeFullProvision: true,
      isFullProvision: true,
      genderOptions: gender,
      getStatusColor,
      buttonLabel: this.$t('Save'),
      accountOfficerFormData: {
        lastName: '',
        otherNames: '',
        staffID: '',
        phoneNumber: '',
        accessLevel: null,
        gender: null,
        address: '',
        branchCode: '',
        status: '',
        id: '',
        name: '',
      },
      filter: {
        accountNumber: '',
        productCode: '',
        branch: '',
        classificationStatus: '',
        provisionDate: '',
        sortColumn: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: '',
        defaultDate: '',
      },
      // classificationOptions: classificationLists,
      accessLevelOptions: accessLevel,
      provisionedOverdraftList: [],
      exportSearchData: [],
      summaryList: [],
      productOptions: [],
      row: [],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
      currentFinancialDate: 'operations/getCurrentFinancialDate',
    }),
    select() {
      return this.provisionedOverdraftList.filter(module => module.select)
    },
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    disabled() {
      return !this.filter.provisionDate || !this.provisionedOverdraftList.length
    },
    getPreviousFinancialDates() {
      return getMinusOneDayDate(this.currentFinancialDate.financialDate)
    },
    exportItems() {
      return this.exportSearchData.map((item, index) => ({
        'S/N': index + 1,
        'Account Name': item.accountName,
        'Loan Account No': item.accountNumber,
        'Linked Account No': item.linkedNumber,
        'Product Name': item.product,
        Branch: item.branch,
        'Amount Overdrawn': item.amountOverdrawn ? formatAmount(item.amountOverdrawn) : 0,
        'Accrued OD Interest': item.accruedODInterest ? formatAmount(item.accruedODInterest) : 0,
        'Amount Above Limit': item.amountAboveLimit ? formatAmount(item.amountAboveLimit) : 0,
        'Number Of Days Above Limit': item.numberOfDaysAboveLimit ? formatAmount(item.numberOfDaysAboveLimit) : 0,
        'Expired Date': item.expiryDate ? this.toDateWithYearFirst(item.expiryDate) : '',
        'Days Past Due': item.daysPastDue,
        'OD Status Classification': item.classificationStatus,
        'Last Provisioned Amount': item.lastProvisonedAmount ? formatAmount(item.lastProvisonedAmount) : 0,
        'Expected Provision Amount': item.expectedProvisionAmount ? formatAmount(item.expectedProvisionAmount) : 0,
        'Increase/Decrease in Provision': item.increaseOrDecrease,
        'Last Provisioned Date': item.lastProvisionedDate ? this.toDateWithYearFirst(item.lastProvisionedDate) : '',
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedSummaryTableHeaders() {
      return this.summaryTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    classificationOptions() {
      return classificationLists.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchProvisionedOverdraft(this.isFullProvision)
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchProvisionedOverdraft(this.isFullProvision)
    },
  },
  mounted() {
    this.datePickerConfig.maxDate = this.getPreviousFinancialDates
    this.fetchOverdraftProducts()
  },
  methods: {
    ...mapActions({
      getProvisionedOverdraft: 'account/getProvisionedOverdraft',
      getProvisionedOverdraftSummary: 'account/getProvisionedOverdraftSummary',
      getProducts: 'account/getProducts',
      postOverdraftProvision: 'account/postOverdraftProvision',
      getBranchByCode: 'operations/getBranchByCode',
      downloadOverdraftProvisioningReport: 'reports/downloadOverdraftProvisioningReport',

    }),
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.overdraftProvisioningRules.validate().then(async success => {
        if (success) {
          this.fetchProvisionedOverdraft()
        }
      })
    },
    async fetchProvisionedOverdraft() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getProvisionedOverdraft({
          search: { ...this.filter, ProvisionDate: this.toDateWithYearFirst(this.filter.provisionDate) },
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data.length) {
          if (this.isFullProvision) {
            this.provisionedOverdraftList = data.data.map(item => ({
              ...item,
              select: true,
            }))
          } else {
            this.provisionedOverdraftList = data.data.map(item => ({
              ...item,
              select: false,
            }))
          }
          this.totalCount = data.recordCount
        } else {
          this.provisionedOverdraftList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async onMakeFullProvision(value) {
      this.isFullProvision = value
      this.fetchProvisionedOverdraft(this.isFullProvision)
    },
    postProvision() {
      const provisionOverdraftAction = async () => {
        try {
          const res = this.provisionedOverdraftList.filter(item => item.select === true)
          if (res.length) {
            this.showSpinner = true
            const response = await this.postOverdraftProvision({
              provisions: res,
              isCurrentFinancialDate: false,
              provisionedDate: this.toDateWithYearFirst(this.filter.provisionedDate),
              initiatedBy: this.userDetails.email,
            })
            const { status, data } = response
            if (status === 200 && data) {
              const requestText = this.$t('Your request to Post Provision Overdraft was successful. Post Provision Overdraft reference number is')
              this.alertSuccess({ text: `${requestText} ${data.referenceNumber}` })
            }
          } else {
            this.alertError({ text: this.$t('Please select overdraft you will like to provision') })
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(provisionOverdraftAction, { title: '', text: this.$t('Are you sure you want to post these Provision Overdraft?') })
    },
    async fetchProvisionSummary() {
      try {
        this.showSpinner = true
        const response = await this.getProvisionedOverdraftSummary({
          provisionDate: this.toDateWithYearFirst(this.filter.provisionedDate),
        })
        this.summaryList = response.data.data.map(item => ({
          ...item,
          expectedProvisionAmount: toBalance(item.expectedProvisionAmount),
          totalPrincipalOutstanding: toBalance(item.totalPrincipalOutstanding),
        }))
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async fetchOverdraftProducts() {
      try {
        const response = await this.getProducts({
          search: { type: 'Savings' },
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_OVERDRAFT_PROVISIONING,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.map(item => ({
            label: item.name,
            value: item.code,
          }))
        } else {
          this.productOptions = []
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async csvExport() {
      try {
        if (this.filter.provisionedDate === '') {
          this.alertError({ text: this.$t('Provision Date must not be empty') })
        } else {
          this.isBusy = true
          const filterData = { ...this.filter, makeFullProvision: this.makeFullProvision }
          const response = await this.downloadOverdraftProvisioningReport({
            search: filterData,
            page: this.currentPage,
            limit: this.perPage,
            fileExportFormat: 1,
            permission: accountPermissions.VIEW_OVERDRAFT_PROVISIONING,
          })
          if (response.status === 200) {
            this.alertSuccessCustom({
              text: this.$t('Your download is in progress and might take a little longer. Go to your'),
              link: {
                text: this.$t('Download Manager'),
                route: { name: 'download-manager' },
              },
              afterLinkText: this.$t('to access the completed file.'),
            })
          } else if (response.status === 504) {
            this.alertSuccessCustom({
              text: this.$t('Your download request is still processing, keep checking the'),
              link: {
                text: this.$t('Download Manager'),
                route: { name: 'download-manager' },
              },
              afterLinkText: this.$t('for status.'),
            })
          }
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        if (this.filter.provisionedDate === '') {
          this.alertError({ text: this.$t('Provision Date must not be empty') })
        } else {
          this.isBusy = true
          const filterData = { ...this.filter, makeFullProvision: this.makeFullProvision }
          const response = await this.downloadOverdraftProvisioningReport({
            search: filterData,
            page: this.currentPage,
            limit: this.perPage,
            fileExportFormat: 0,
            permission: accountPermissions.VIEW_OVERDRAFT_PROVISIONING,
          })
          if (response.status === 200) {
            this.alertSuccessCustom({
              text: this.$t('Your download is in progress and might take a little longer. Go to your'),
              link: {
                text: this.$t('Download Manager'),
                route: { name: 'download-manager' },
              },
              afterLinkText: this.$t('to access the completed file.'),
            })
          } else if (response.status === 504) {
            this.alertSuccessCustom({
              text: this.$t('Your download request is still processing, keep checking the'),
              link: {
                text: this.$t('Download Manager'),
                route: { name: 'download-manager' },
              },
              afterLinkText: this.$t('for status.'),
            })
          }
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchProvisionedOverdraft()
    },
  },
}
</script>
